<script setup lang="ts">
  import { Execution } from '@/shared/Executions/models/Execution'
  import { ExecutionEngineType } from '@/shared/Executions/models/ExecutionEngineType'
  import { ExecutionPhaseType } from '@/shared/Executions/models/ExecutionPhaseType'
  import { IExecutionPhaseService } from '@/shared/Executions/services/ExecutionPhaseService'
  import { BookText, Calculator, CircleAlert, Cog, FileLineChart, TableIcon } from 'lucide-vue-next'
  import { computed } from 'vue'

  const props = defineProps<{
    record: Execution
    index?: number
    selectable?: boolean
    onCellClick?: (record: Execution, e?: Event) => void
    excecutionPhaseService: IExecutionPhaseService
  }>()

  function handleClick(e?: Event) {
    if (props.selectable && props.onCellClick) {
      props.onCellClick(props.record, e)
    }
  }

  const phaseContainsPKAnalysis = computed(
    () =>
      props.excecutionPhaseService.tryGetExecutionPhaseForPhaseType(
        props.record,
        ExecutionPhaseType.NCAAnalysis
      ) != null
  )

  const phaseContainsCustomCalculations = computed(
    () =>
      props.excecutionPhaseService.tryGetExecutionPhaseForPhaseType(
        props.record,
        ExecutionPhaseType.CustomCalculations
      ) != null
  )

  const phaseContainsPlots = computed(
    () =>
      props.excecutionPhaseService
        .getExecutionEngineVersionsForExecution(
          props.record
        )
        .find((x) => x.package === ExecutionEngineType.AplosNcaCustomPlotting) != null
  )

  const phaseContainsTables = computed(
    () =>
      props.excecutionPhaseService
        .getExecutionEngineVersionsForExecution(
          props.record
        )
        .find((x) => x.package === ExecutionEngineType.AplosNcaCustomTables) != null
  )

  const phaseContainsCustomReports = computed(
    () =>
      props.excecutionPhaseService.tryGetExecutionPhaseForPhaseType(
        props.record,
        ExecutionPhaseType.Reports
      ) != null
  )

  const executionTooltip = computed(() => {
    const parts: string[] = []
    if (phaseContainsPKAnalysis.value) parts.push('PK Analysis')
    if (phaseContainsCustomCalculations.value) parts.push('Custom Calculations')
    if (phaseContainsTables.value) parts.push('Tables')
    if (phaseContainsPlots.value) parts.push('Plots')
    if (phaseContainsCustomReports.value) parts.push('Custom Report')
    return parts.length > 0 ? parts.join(', ') : 'No Output'
  })
</script>

<template>
  <div :class="{ 'cursor-pointer': selectable }" @click="handleClick">
    <a-tooltip>
      <template #title>
        <div>
          <div>
            <b>Type:</b>
            {{ executionTooltip }}
          </div>
        </div>
      </template>
      <div class="flex">
        <div v-if="phaseContainsPKAnalysis">
          <cog class="size-4" />
        </div>
        <div v-if="phaseContainsCustomCalculations">
          <calculator class="size-4" />
        </div>
        <div v-if="phaseContainsPlots">
          <file-line-chart class="size-4" />
        </div>
        <div v-if="phaseContainsTables">
          <table-icon class="size-4" />
        </div>
        <div v-if="phaseContainsCustomReports">
          <book-text class="size-4" />
        </div>
        <div v-if="executionTooltip == 'No Output'"> 
          <circle-alert class="size-4" />
        </div>
      </div>
    </a-tooltip>
  </div>
</template>
